import { useNavigate } from "react-router-dom";
import { z } from "zod";
import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { AdminJobDetails } from "@/queries/admin/job-details";

export const adminCreateCompanyFormSchema = z.object({
  name: z.string({ required_error: "Name is required" }).min(1),
  companyLogo: z.instanceof(File).optional(),
  linkedIn: z.preprocess(
    (arg) => {
      if (typeof arg === "string" && arg === "") {
        return undefined;
      } else {
        return arg;
      }
    },
    z
      .string()
      .regex(/^(http(s)?:\/\/)?www\.?linkedin\.com\/.*$/, {
        message: 'A LinkedIn URL should start with "https://www.linkedin.com/"',
      })
      .optional(),
  ),
  websiteLink: z.string().optional(),
});

export type AdminCreateCompanyFormSchema = z.infer<
  typeof adminCreateCompanyFormSchema
>;

export const useCreateCompany = () => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const navigate = useNavigate();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: AdminCreateCompanyFormSchema) => {
      const token = await getAccessTokenSilently();
      const body = buildFormData(data);

      return request<AdminJobDetails>({
        method: "POST",
        path: `/api/admin/companies`,
        token,
        body,
        contentType: "application/x-www-form-urlencoded",
      });
    },
    onSuccess: (data) => {
      // TODO: Handle success response better
      // Invalidate pipeline data
      void qc.invalidateQueries({
        queryKey: QueryKeys.adminDashboard,
      });

      void qc.invalidateQueries({
        queryKey: QueryKeys.adminCompanies,
      });

      navigate(`/new-admin/company/${data.id}`);
    },
  });

  return {
    createCompany: mutate,
    isCreatingCompany: isPending,
    createCompanyError: error,
  };
};

function buildFormData(data: AdminCreateCompanyFormSchema) {
  const body = new FormData();

  body.append("name", data.name);

  if (data.linkedIn) {
    body.append("linkedIn", data.linkedIn);
  }

  if (data.websiteLink) {
    body.append("websiteLink", data.websiteLink);
  }

  if (data.companyLogo) {
    body.append("companyLogo", data.companyLogo);
  }

  return body;
}
